import React, { JSX, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import classNames from 'classnames'
import {
  ChartBarIcon,
  TrophyIcon,
  ArrowsRightLeftIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  QuestionMarkCircleIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { YouTubeAreaIconLink } from 'components/YouTubeAreaIconLink'
import { useAuth } from 'context/user'
import { useEventAccess } from 'hooks/useEventAccess'
import { useEventDetail } from 'hooks/useEventDetail'
import { useYouTubeConnection } from 'hooks/useYouTubeConnection'
import { useEventYouTubeSessions } from 'hooks/useYouTubeSessions'
import { useDefaultOverlayLink, buildOverlayShortUrl } from 'hooks/useOverlayLinks'
import { EventYouTubeSources, LiveDot } from 'components/EventYouTubeSources'
import { TriviaLeaderboardPanel } from 'components/TriviaLeaderboardPanel'
import { getInitials } from 'lib/helpers'
import { eventSectionPath } from 'lib/paths'
import { compareByName, elementDisplayName, sortByName } from 'lib/elementOrder'
import {
  VoteLaneMode,
  largestRemainderPercents,
  sumCounts,
  twitchChannelCountOf,
  voteLaneModeOf
} from 'lib/votePercent'
import { RESET_STREAM_KEY_CONFIRM, rotateStreamKey } from 'lib/streamKey'
import {
  EventDetail,
  EventYouTubeForeignSource,
  PollSummary,
  TriviaSummary,
  TugSummary,
  ScoreboardSummary
} from 'types/api'

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const SubLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <div className='mb-2 text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </div>
)

const CopyButton = ({ value, label }: { value: string, label: string }): JSX.Element => {
  const [copied, setCopied] = useState(false)
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      globalThis.setTimeout(() => setCopied(false), 1500)
    } catch {
    }
  }
  return (
    <button
      type='button'
      onClick={() => { void handleCopy() }}
      title={`Copy ${label}`}
      className='rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
    >
      {copied
        ? <ClipboardDocumentCheckIcon className='size-4 text-emerald-400' aria-hidden='true' />
        : <ClipboardDocumentIcon className='size-4' aria-hidden='true' />}
      <span className='sr-only'>Copy {label}</span>
    </button>
  )
}

const OpenLinkButton = ({ href, label }: { href: string, label: string }): JSX.Element => (
  <a
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    title={`Open ${label}`}
    className='rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
  >
    <ArrowTopRightOnSquareIcon className='size-4' aria-hidden='true' />
    <span className='sr-only'>Open {label}</span>
  </a>
)

const OverlayLinkRow = ({
  title,
  url,
  icon: Icon
}: {
  title: string
  url: string | null
  icon: React.ForwardRefExoticComponent<Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
    title?: string
    titleId?: string
  } & React.RefAttributes<SVGSVGElement>>
}): JSX.Element => {
  if (url === null) {
    return <div className='h-[54px] animate-pulse rounded-lg bg-white/5' aria-hidden='true' />
  }
  return (
    <div className='flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2.5'>
      <div className='flex min-w-0 items-center gap-3'>
        <Icon className='size-5 shrink-0 text-dm-text-accent' aria-hidden='true' />
        <div className='flex min-w-0 flex-col leading-tight'>
          <span className='text-sm font-medium text-dm-text-primary'>{title}</span>
          <span className='truncate text-[11px] text-brand'>{url}</span>
        </div>
      </div>
      <div className='flex shrink-0 items-center gap-0.5'>
        <CopyButton value={url} label={`${title} link`} />
        <OpenLinkButton href={url} label={title} />
      </div>
    </div>
  )
}

const ResetStreamKeyControl = ({
  eventId,
  afterReset
}: {
  eventId: string
  afterReset?: () => Promise<void>
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const [resetBusy, setResetBusy] = useState(false)
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetDone, setResetDone] = useState(false)

  const handleReset = async (): Promise<void> => {
    if (auth0Token === undefined || resetBusy) return
    if (!globalThis.confirm(RESET_STREAM_KEY_CONFIRM)) return
    setResetBusy(true)
    setResetError(null)
    setResetDone(false)
    try {
      await rotateStreamKey(eventId, auth0Token)
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Could not reset the stream key.')
      setResetBusy(false)
      return
    }
    setResetDone(true)
    try {
      if (afterReset !== undefined) await afterReset()
    } finally {
      setResetBusy(false)
    }
  }

  return (
    <div className='mt-4 rounded-md border border-red-500/20 bg-red-500/5 px-3 py-2.5'>
      <div className='flex items-center justify-between gap-3'>
        <p className='min-w-0 text-[11px] text-dm-text-accent'>
          Key leaked, shown on stream or shared too widely? Resetting turns
          off every copy of this stream&apos;s key: the desktop and mobile
          overlay URLs, every camera operator page, and any older overlay
          URLs. Everyone re-pastes the new links. Quick Overlay links and
          merch board links are separate and keep working.
        </p>
        <button
          type='button'
          onClick={() => { void handleReset() }}
          disabled={resetBusy}
          title='Turn off every copy of the stream key, then show a fresh one.'
          className='inline-flex shrink-0 items-center gap-1.5 rounded-md border border-red-500/40 px-3 py-1.5 text-xs font-medium text-red-300 hover:border-red-500/70 hover:text-red-200 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {resetBusy ? <LoadingSpinner /> : <ShieldExclamationIcon className='size-3.5' aria-hidden='true' />}
          Reset stream key
        </button>
      </div>
      {resetError !== null && (
        <p className='mt-2 text-[11px] text-red-400'>
          {resetError}
        </p>
      )}
      {resetDone && resetError === null && (
        <p className='mt-2 text-[11px] text-emerald-400' role='status'>
          Stream key reset. Re-paste the new link anywhere you use it.
        </p>
      )}
    </div>
  )
}

const LiveControlCard = ({ event }: { event: EventDetail }): JSX.Element => {
  const { k, error, recreate } = useDefaultOverlayLink(event.eventId)
  const buildUrl = (kind: 'composite' | 'composite-mobile'): string | null =>
    buildOverlayShortUrl(event.slug, kind, k)

  const { canManage } = useEventAccess()

  return (
    <SectionCard title='Live Control'>
      <SubLabel>Browser Source URLs</SubLabel>
      <div className='space-y-2'>
        <OverlayLinkRow title='Desktop Overlay' url={buildUrl('composite')} icon={ComputerDesktopIcon} />
        <OverlayLinkRow title='Mobile Overlay' url={buildUrl('composite-mobile')} icon={DevicePhoneMobileIcon} />
      </div>

      {error !== null && (
        <p className='mt-3 text-[11px] text-red-400'>
          {error}
        </p>
      )}

      <p className='mt-3 text-[11px] text-dm-text-accent'>
        Both URLs carry this stream&apos;s key. Everyone on this stream sees
        the same key, and it never expires. Treat it like a password: keep
        it off stream, and paste it rather than retyping it. Only an owner
        can reset it.
      </p>
      {canManage && <ResetStreamKeyControl eventId={event.eventId} afterReset={recreate} />}

      <p className='mt-4 text-[11px] text-dm-text-accent'>
        Add each URL as a Browser Source with a transparent
        background. The desktop and mobile composite overlays each render
        whatever scene is currently live for that kind, and nothing
        otherwise. Chat overlays and emote walls made in this stream show
        through this stream&apos;s key. Quick Overlays and merch boards
        have their own links, and this stream&apos;s key does not affect
        them.
      </p>
    </SectionCard>
  )
}

const ConnectedSourcesCard = ({
  event,
  onForeignSourcesChange
}: {
  event: EventDetail
  onForeignSourcesChange: (sources: EventYouTubeForeignSource[]) => void
}): JSX.Element => {
  const youtubeAttached = event.youtubeSourceAttached === true
  const foreignCount = event.youtubeForeignSources?.length ?? 0
  const { superuser, canManage } = useEventAccess()
  const store = useEventYouTubeSessions(event.eventId, youtubeAttached || foreignCount > 0)
  const ownLive = store.sessions.some((s) =>
    s.status === 'RUNNING' && s.foreignEvent !== true && s.eventId === event.eventId
  )
  const { youtubeChannelName } = useYouTubeConnection()
  let body: JSX.Element | null
  if (event.channels.length === 0 && !youtubeAttached && foreignCount > 0) {
    body = null
  } else if (event.channels.length === 0 && !youtubeAttached) {
    body = (
      <div className='rounded-lg border border-dashed border-white/10 px-3 py-4 text-center text-xs text-dm-text-secondary'>
        No sources yet, add them on the Integrations page.
      </div>
    )
  } else {
    body = (
      <ul className='space-y-2'>
        {event.channels.map((c) => (
          <li
            key={c.broadcasterUserId}
            className='flex items-center gap-2.5 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2'
          >
            <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
            <span className='truncate text-sm text-dm-text-primary'>#{c.channelLogin}</span>
          </li>
        ))}
        {youtubeAttached && (
          <li
            data-testid='overview-youtube-source'
            className='flex items-center gap-2.5 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2'
          >
            <YouTubeAreaIconLink size='sm' />
            <span className='min-w-0 flex-1 truncate text-sm text-dm-text-primary'>
              {youtubeChannelName ?? 'Your YouTube channel'}
            </span>
            {ownLive && <LiveDot testId='overview-youtube-source-live' />}
            <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
              attached
            </span>
          </li>
        )}
      </ul>
    )
  }
  return (
    <SectionCard title='Connected sources'>
      {body}
      <EventYouTubeSources
        event={event}
        store={store}
        superuser={superuser}
        canManage={canManage}
        onForeignSourcesChange={onForeignSourcesChange}
      />
    </SectionCard>
  )
}

const ContentSummaryCard = ({ event }: { event: EventDetail }): JSX.Element => (
  <SectionCard title='Content Summary'>
    <ul className='space-y-2'>
      {[
        { name: 'Scoreboard', count: event.scoreboards.length, icon: TrophyIcon },
        { name: 'Polls', count: event.polls.length, icon: ChartBarIcon },
        { name: 'Trivia', count: event.trivias.length, icon: QuestionMarkCircleIcon },
        { name: 'Tug of Wars', count: event.tugs.length, icon: ArrowsRightLeftIcon }
      ].map(({ name, count, icon: Icon }) => (
        <li
          key={name}
          className='flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2.5'
        >
          <div className='flex items-center gap-2.5'>
            <Icon className='size-5 text-brand' aria-hidden='true' />
            <span className='text-sm font-medium text-dm-text-primary'>{name}</span>
          </div>
          <span className='text-sm font-semibold text-dm-text-secondary'>{count}</span>
        </li>
      ))}
    </ul>
  </SectionCard>
)

const UserAccessCard = ({
  event,
  currentUserId,
  currentDisplayName,
  currentEmail,
  onManage
}: {
  event: EventDetail
  currentUserId: string | undefined
  currentDisplayName: string | undefined
  currentEmail: string | undefined
  onManage: () => void
}): JSX.Element => {
  const ordered = [...event.members].sort((a, b) => {
    if (a.role !== b.role) return a.role === 'owner' ? -1 : 1
    const an = (a.displayName ?? a.email ?? '').toLowerCase()
    const bn = (b.displayName ?? b.email ?? '').toLowerCase()
    return an.localeCompare(bn)
  })

  const membersEmpty = (
    <div className='rounded-lg border border-dashed border-white/10 px-3 py-4 text-center text-xs text-dm-text-secondary'>
      No members yet.
    </div>
  )
  const membersList = (
    <ul className='space-y-2'>
      {ordered.map((m) => {
        const isMe = m.userId === currentUserId
        const displayName = isMe ? (currentDisplayName ?? m.displayName) : m.displayName
        const email = isMe ? (currentEmail ?? m.email) : m.email
        const display = displayName ?? email ?? 'Unknown user'
        const initials = getInitials(displayName ?? email)
        const isOwner = m.role === 'owner'
        return (
          <li
            key={m.userId}
            className='flex items-center justify-between gap-3 rounded-lg px-1 py-1.5'
          >
            <div className='flex min-w-0 items-center gap-2.5'>
              <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/15 text-[11px] font-semibold text-brand'>
                {initials}
              </span>
              <span className='truncate text-sm text-dm-text-primary'>
                {display}{isMe && <span className='ml-1.5 text-[10px] uppercase tracking-wider text-dm-text-accent'>(You)</span>}
              </span>
            </div>
            <span
              className={classNames(
                'text-xs font-medium capitalize',
                isOwner ? 'text-brand' : 'text-dm-text-secondary'
              )}
            >
              {m.role}
            </span>
          </li>
        )
      })}
    </ul>
  )

  return (
    <SectionCard
      title='User Access'
      action={
        <button
          type='button'
          onClick={onManage}
          className='rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
        >
          Manage
        </button>
      }
    >
      {ordered.length === 0 ? membersEmpty : membersList}
    </SectionCard>
  )
}

const pct = (count: number, total: number): number =>
  total <= 0 ? 0 : Math.round((count / total) * 100)

const formatDuration = (startIso: string | null, endIso: string | null): string => {
  if (startIso === null || endIso === null) return '—'
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime()
  if (!Number.isFinite(ms) || ms <= 0) return '—'
  const mins = Math.round(ms / 60000)
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

const StatTile = ({ label, value }: { label: string, value: string | number }): JSX.Element => (
  <div className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2.5'>
    <div className='text-lg font-semibold text-dm-text-primary'>{value}</div>
    <div className='text-[11px] uppercase tracking-wider text-dm-text-accent'>{label}</div>
  </div>
)

const SuperlativeTile = ({
  label, value, detail
}: { label: string, value: string, detail?: string }): JSX.Element => (
  <div className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2.5'>
    <div className='text-[11px] uppercase tracking-wider text-dm-text-accent'>{label}</div>
    <div className='mt-0.5 truncate text-sm font-semibold text-dm-text-primary' title={value}>{value}</div>
    {detail !== undefined && <div className='truncate text-[11px] text-dm-text-accent'>{detail}</div>}
  </div>
)

const ResultBar = ({
  label, sublabel, count, percent, color, winner
}: {
  label: string
  sublabel?: string
  count: number
  percent: number
  color: string
  winner: boolean
}): JSX.Element => {
  const p = percent
  return (
    <div className={classNames('rounded-md px-2.5 py-2', winner && 'bg-white/5 ring-1 ring-brand/30')}>
      <div className='mb-1 flex items-baseline justify-between gap-2 text-sm'>
        <span className={classNames('min-w-0 truncate', winner ? 'font-semibold text-dm-text-primary' : 'text-dm-text-secondary')}>
          {label}
          {sublabel !== undefined && <span className='ml-1.5 font-mono text-[11px] text-dm-text-accent'>{sublabel}</span>}
        </span>
        <span className={classNames('shrink-0 tabular-nums', winner ? 'font-semibold text-brand' : 'text-dm-text-accent')}>
          {count} · {p}%
        </span>
      </div>
      <div className='h-2 overflow-hidden rounded-full bg-white/10'>
        <div className='h-full rounded-full' style={{ width: `${p}%`, background: color }} />
      </div>
    </div>
  )
}

type Platform = 'twitch' | 'youtube'

interface ElementLanes {
  mode: VoteLaneMode
  twitch: number[]
  youtube: number[]
  twitchTotal: number
  youtubeTotal: number
}

const lanesOf = (
  counts: Array<{ voteCount: number, voteCountByPlatform?: { twitch: number, youtube: number } | null }>,
  broadcasterUserIds: string[] | null | undefined
): ElementLanes => {
  const twitch = counts.map((c) => c.voteCountByPlatform?.twitch ?? c.voteCount)
  const youtube = counts.map((c) => c.voteCountByPlatform?.youtube ?? 0)
  const twitchTotal = sumCounts(twitch)
  const youtubeTotal = sumCounts(youtube)
  return {
    mode: voteLaneModeOf({ twitchChannelCount: twitchChannelCountOf(broadcasterUserIds), twitchTotal, youtubeTotal }),
    twitch,
    youtube,
    twitchTotal,
    youtubeTotal
  }
}

const pollLanesOf = (poll: PollSummary): ElementLanes => lanesOf(poll.options, poll.broadcasterUserIds)
const tugLanesOf = (tug: TugSummary): ElementLanes => lanesOf([tug.optionA, tug.optionB], tug.broadcasterUserIds)

const shownLanesOf = (lanes: ElementLanes, flat: number[]): Array<{ platform: Platform, counts: number[] }> =>
  lanes.mode === 'SPLIT'
    ? [{ platform: 'twitch', counts: lanes.twitch }, { platform: 'youtube', counts: lanes.youtube }]
    : lanes.mode === 'YOUTUBE'
      ? [{ platform: 'youtube', counts: lanes.youtube }]
      : [{ platform: 'twitch', counts: flat }]

const laneMarginOf = (counts: number[]): number => {
  const pcts = largestRemainderPercents(counts)
  const ranked = counts
    .map((c, i) => ({ c, p: pcts[i] ?? 0 }))
    .sort((a, b) => b.c - a.c)
  const top = ranked[0]
  const runnerUp = ranked[1]
  if (top === undefined || runnerUp === undefined || top.c === runnerUp.c) return 0
  return top.p - runnerUp.p
}

const PlatformBlock = ({ platform, children }: { platform: Platform, children: React.ReactNode }): JSX.Element => (
  <div data-testid={`recap-lane-${platform}`}>
    <div className='mb-1 flex items-center gap-1.5 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-dm-text-accent'>
      {platform === 'twitch' ? <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' /> : <YouTubeAreaIconLink size='sm' />}
      {platform === 'twitch' ? 'Twitch' : 'YouTube'}
    </div>
    {children}
  </div>
)

const PollLaneResult = ({ poll, counts }: { poll: PollSummary, counts: number[] }): JSX.Element => {
  const pcts = largestRemainderPercents(counts)
  const ranked = poll.options
    .map((o, i) => ({ o, count: counts[i] ?? 0, pct: pcts[i] ?? 0 }))
    .sort((a, b) => b.count - a.count)
  const top = ranked[0]?.count ?? 0
  const runnerUp = ranked[1]?.count ?? 0
  const margin = laneMarginOf(counts)
  return (
    <div className='space-y-1.5'>
      {ranked.map(({ o, count, pct: p }) => (
        <ResultBar
          key={o.optionId}
          label={o.displayText.length > 0 ? o.displayText : o.command}
          sublabel={o.command}
          count={count}
          percent={p}
          color={o.progressBarColor}
          winner={top > 0 && count === top}
        />
      ))}
      {ranked.length >= 2 && top > 0 && (
        <p className='pt-1 text-[11px] text-dm-text-accent'>
          {runnerUp === top
            ? <>Tied at {top} vote{top === 1 ? '' : 's'}.</>
            : <>Won by {margin} point{margin === 1 ? '' : 's'} ({top} vs {runnerUp} votes).</>}
        </p>
      )}
    </div>
  )
}

const PollResultBody = ({ poll }: { poll: PollSummary }): JSX.Element => {
  const lanes = shownLanesOf(pollLanesOf(poll), poll.options.map((o) => o.voteCount))
  if (lanes.length === 1) return <PollLaneResult poll={poll} counts={lanes[0].counts} />
  return (
    <div className='space-y-3'>
      {lanes.map((l) => (
        <PlatformBlock key={l.platform} platform={l.platform}>
          <PollLaneResult poll={poll} counts={l.counts} />
        </PlatformBlock>
      ))}
    </div>
  )
}

const TugLaneResult = ({ tug, a, b }: { tug: TugSummary, a: number, b: number }): JSX.Element => {
  const total = a + b
  const [pa, pb] = largestRemainderPercents([a, b])
  const aTop = a >= b
  const bTop = b >= a
  const winSide = aTop ? tug.optionA : tug.optionB
  const winLabel = winSide.displayText.length > 0 ? winSide.displayText : winSide.command
  return (
    <div className='space-y-1.5'>
      <ResultBar
        label={tug.optionA.displayText.length > 0 ? tug.optionA.displayText : tug.optionA.command}
        sublabel={tug.optionA.command}
        count={a} percent={pa} color={tug.optionA.color} winner={aTop && total > 0}
      />
      <ResultBar
        label={tug.optionB.displayText.length > 0 ? tug.optionB.displayText : tug.optionB.command}
        sublabel={tug.optionB.command}
        count={b} percent={pb} color={tug.optionB.color} winner={bTop && total > 0}
      />
      {total > 0 && (
        <p className='pt-1 text-[11px] text-dm-text-accent'>
          {a === b
            ? <>Tied at {a} vote{a === 1 ? '' : 's'}.</>
            : `${winLabel} won by ${Math.abs(pa - pb)} points.`}
        </p>
      )}
    </div>
  )
}

const TugResultBody = ({ tug }: { tug: TugSummary }): JSX.Element => {
  const lanes = shownLanesOf(tugLanesOf(tug), [tug.optionA.voteCount, tug.optionB.voteCount])
  if (lanes.length === 1) return <TugLaneResult tug={tug} a={lanes[0].counts[0] ?? 0} b={lanes[0].counts[1] ?? 0} />
  return (
    <div className='space-y-3'>
      {lanes.map((l) => (
        <PlatformBlock key={l.platform} platform={l.platform}>
          <TugLaneResult tug={tug} a={l.counts[0] ?? 0} b={l.counts[1] ?? 0} />
        </PlatformBlock>
      ))}
    </div>
  )
}

type ResultItem =
  | { kind: 'poll', poll: PollSummary }
  | { kind: 'tug', tug: TugSummary }

const voteLineOf = (flatTotal: number, lanes: ElementLanes): string => {
  if (lanes.mode === 'YOUTUBE') return `${lanes.youtubeTotal} YouTube vote${lanes.youtubeTotal === 1 ? '' : 's'}`
  if (lanes.mode === 'SPLIT') return `Twitch ${lanes.twitchTotal} · YouTube ${lanes.youtubeTotal}`
  return `${flatTotal} vote${flatTotal === 1 ? '' : 's'}`
}

const ResultsCarousel = ({ items }: { items: ResultItem[] }): JSX.Element => {
  const [idx, setIdx] = useState(0)
  const safeIdx = Math.min(idx, items.length - 1)
  const item = items[safeIdx]
  if (item === undefined) return <></>
  const go = (delta: number): void => {
    setIdx(() => {
      const n = items.length
      return ((safeIdx + delta) % n + n) % n
    })
  }
  const meta = item.kind === 'poll'
    ? { title: elementDisplayName(item.poll.name, item.poll.question), question: item.poll.question, voteLine: voteLineOf(item.poll.totalVotes, pollLanesOf(item.poll)) }
    : { title: elementDisplayName(item.tug.name, item.tug.question), question: item.tug.question, voteLine: voteLineOf(item.tug.totalVotes, tugLanesOf(item.tug)) }
  const isPoll = item.kind === 'poll'
  return (
    <div>
      <div className='mb-3 flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-2'>
          <span className={classNames(
            'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
            isPoll ? 'bg-sky-500/15 text-sky-300' : 'bg-fuchsia-500/15 text-fuchsia-300'
          )}
          >
            {isPoll
              ? <ChartBarIcon className='size-3' aria-hidden='true' />
              : <ArrowsRightLeftIcon className='size-3' aria-hidden='true' />}
            {isPoll ? 'Poll' : 'Tug of War'}
          </span>
          <span className='truncate text-sm font-medium text-dm-text-primary'>{meta.title}</span>
        </div>
        <span className='shrink-0 text-[11px] tabular-nums text-dm-text-accent'>
          {safeIdx + 1} / {items.length}
        </span>
      </div>
      <p className='mb-3 text-xs text-dm-text-secondary'>
        {meta.question} · <span className='text-dm-text-accent'>{meta.voteLine}</span>
      </p>
      {item.kind === 'poll'
        ? <PollResultBody poll={item.poll} />
        : <TugResultBody tug={item.tug} />}
      {items.length > 1 && (
        <div className='mt-4 flex items-center justify-between'>
          <button
            type='button'
            onClick={() => go(-1)}
            className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
          >
            <ChevronLeftIcon className='size-3.5' aria-hidden='true' /> Prev
          </button>
          <button
            type='button'
            onClick={() => go(1)}
            className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
          >
            Next <ChevronRightIcon className='size-3.5' aria-hidden='true' />
          </button>
        </div>
      )}
    </div>
  )
}

const ScoreboardStandings = ({ board }: { board: ScoreboardSummary }): JSX.Element => {
  const ranked = [...board.competitors].sort((a, b) => b.score - a.score)
  const top = ranked[0]?.score ?? 0
  return (
    <div className='rounded-lg border border-white/5 bg-dm-bg-accent/30 p-3'>
      <div className='mb-2 truncate text-sm font-medium text-dm-text-primary'>{board.name}</div>
      <ul className='space-y-1'>
        {ranked.map((c, i) => (
          <li key={c.competitorId} className='flex items-center justify-between gap-2 text-sm'>
            <span className='flex min-w-0 items-center gap-2'>
              <span className={classNames('w-4 shrink-0 text-right text-[11px] tabular-nums', top > 0 && c.score === top ? 'text-brand' : 'text-dm-text-accent')}>
                {i + 1}
              </span>
              <span className={classNames('truncate', top > 0 && c.score === top ? 'font-semibold text-dm-text-primary' : 'text-dm-text-secondary')}>
                {c.name}
              </span>
            </span>
            <span className={classNames('shrink-0 tabular-nums', top > 0 && c.score === top ? 'font-semibold text-brand' : 'text-dm-text-accent')}>
              {c.score}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const TriviaRecapCard = ({ trivia }: { trivia: TriviaSummary }): JSX.Element => {
  const revealed = trivia.revealedAt != null
  const correct = trivia.options.find((o) => o.isCorrect === true)
  const twitchAnswered = trivia.options.reduce((s, o) => s + (o.voteCountByPlatform?.twitch ?? o.voteCount), 0)
  const youtubeAnswered = trivia.options.reduce((s, o) => s + (o.voteCountByPlatform?.youtube ?? 0), 0)
  const twitchCorrect = correct?.voteCountByPlatform?.twitch ?? correct?.voteCount ?? 0
  const youtubeCorrect = correct?.voteCountByPlatform?.youtube ?? 0
  const youtubeRemoved = trivia.youtubeCountsPurgedAt != null
  return (
    <div className='rounded-lg border border-white/5 bg-dm-bg-accent/30 p-3'>
      <div className='mb-1 flex items-center justify-between gap-2'>
        <span className='min-w-0 truncate text-sm font-medium text-dm-text-primary' title={trivia.question}>
          {elementDisplayName(trivia.name, trivia.question)}
        </span>
        <span className={classNames(
          'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
          revealed
            ? 'bg-fuchsia-500/15 text-fuchsia-300 ring-fuchsia-500/30'
            : 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30'
        )}
        >
          {revealed ? 'Revealed' : 'Not revealed'}
        </span>
      </div>
      {revealed
        ? (
          <>
            <p className='text-xs text-dm-text-secondary'>
              Correct answer: <span className='font-medium text-emerald-300'>{correct !== undefined ? correct.displayText : 'unknown'}</span>
            </p>
            <p className='mt-1 text-[11px] text-dm-text-accent'>
              Twitch: {twitchAnswered === 0 ? 'No answers' : `${pct(twitchCorrect, twitchAnswered)}% got it right (${twitchCorrect} / ${twitchAnswered})`}
              {' · '}
              YouTube: {youtubeRemoved
                ? 'removed'
                : youtubeAnswered === 0
                  ? 'No answers'
                  : `${pct(youtubeCorrect, youtubeAnswered)}% got it right (${youtubeCorrect} / ${youtubeAnswered})`}
            </p>
          </>
          )
        : (
          <p className='text-[11px] text-dm-text-accent'>
            Never revealed, so it awarded no leaderboard points.
          </p>
          )}
    </div>
  )
}

const EventRecap = ({ event }: { event: EventDetail }): JSX.Element => {
  const plainPolls = event.polls
  const trivias = event.trivias

  const items = useMemo<ResultItem[]>(() => {
    const polls = plainPolls
      .filter((p) => p.totalVotes > 0 || pollLanesOf(p).youtubeTotal > 0)
      .map((p): ResultItem => ({ kind: 'poll', poll: p }))
    const tugs = event.tugs
      .filter((t) => t.totalVotes > 0 || tugLanesOf(t).youtubeTotal > 0)
      .map((t): ResultItem => ({ kind: 'tug', tug: t }))
    const keyOf = (x: ResultItem): { primary: number, name: string, id: string } => {
      if (x.kind === 'poll') {
        const lanes = pollLanesOf(x.poll)
        return { primary: lanes.mode === 'YOUTUBE' ? lanes.youtubeTotal : x.poll.totalVotes, name: elementDisplayName(x.poll.name, x.poll.question), id: x.poll.pollId }
      }
      const lanes = tugLanesOf(x.tug)
      return { primary: lanes.mode === 'YOUTUBE' ? lanes.youtubeTotal : x.tug.totalVotes, name: elementDisplayName(x.tug.name, x.tug.question), id: x.tug.tugId }
    }
    return [...polls, ...tugs].sort((x, y) => {
      const xk = keyOf(x)
      const yk = keyOf(y)
      const xv = xk.primary
      const yv = yk.primary
      if (yv === xv) return compareByName(xk.name, xk.id, yk.name, yk.id)
      return yv - xv
    })
  }, [plainPolls, event.tugs])

  const boardsWithScores = useMemo(
    () => event.scoreboards.filter((s) => s.competitors.some((c) => c.score !== 0)),
    [event.scoreboards]
  )

  const totalVotes = useMemo(
    () => [...plainPolls, ...event.tugs].reduce((sum, x) => sum + x.totalVotes, 0),
    [plainPolls, event.tugs]
  )
  const youtubeVotes = useMemo(
    () => plainPolls.reduce((sum, p) => sum + pollLanesOf(p).youtubeTotal, 0) +
      event.tugs.reduce((sum, t) => sum + tugLanesOf(t).youtubeTotal, 0),
    [plainPolls, event.tugs]
  )

  const anyYouTubeVotes = youtubeVotes > 0

  const laneEntries = useMemo(() => items.flatMap((it) => {
    const lanes = it.kind === 'poll' ? pollLanesOf(it.poll) : tugLanesOf(it.tug)
    const flat = it.kind === 'poll'
      ? it.poll.options.map((o) => o.voteCount)
      : [it.tug.optionA.voteCount, it.tug.optionB.voteCount]
    const { name, flatTotal } = it.kind === 'poll'
      ? { name: elementDisplayName(it.poll.name, it.poll.question), flatTotal: it.poll.totalVotes }
      : { name: elementDisplayName(it.tug.name, it.tug.question), flatTotal: it.tug.totalVotes }
    return shownLanesOf(lanes, flat)
      .map((l) => ({
        name,
        platform: l.platform,
        totalVotes: lanes.mode === 'TWITCH' ? flatTotal : sumCounts(l.counts),
        margin: laneMarginOf(l.counts)
      }))
      .filter((e) => e.totalVotes > 0)
  }), [items])

  const superlatives = useMemo(() => {
    if (items.length < 2) return null
    const first = laneEntries[0]
    if (first === undefined) return null
    return {
      mostVotes: laneEntries.reduce(
        (best, x) => (x.totalVotes > best.totalVotes ? x : best),
        first
      ),
      closest: laneEntries.reduce(
        (best, x) => (x.margin < best.margin ? x : best),
        first
      ),
      landslide: laneEntries.reduce(
        (best, x) => (x.margin > best.margin ? x : best),
        first
      )
    }
  }, [items, laneEntries])

  const avgVotes = items.length > 0 ? Math.round(totalVotes / items.length) : 0
  const avgOf = (platform: Platform): number => {
    const own = laneEntries.filter((e) => e.platform === platform)
    return own.length > 0 ? Math.round(own.reduce((s, e) => s + e.totalVotes, 0) / own.length) : 0
  }
  const youtubeTag = (platform: Platform): string => platform === 'youtube' ? ' (YouTube)' : ''

  const rightColHasContent = superlatives !== null || boardsWithScores.length > 0

  const resultsCarousel = (
    <div className='rounded-lg border border-white/5 bg-dm-bg-accent/20 p-4'>
      <ResultsCarousel items={items} />
    </div>
  )

  return (
    <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5 xl:col-span-2'>
      <div className='mb-4 flex items-center justify-between'>
        <h3 className='text-base font-semibold text-dm-text-primary'>Stream Recap</h3>
        <span className='rounded-md border border-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          Final
        </span>
      </div>
      <div className={classNames('mb-5 grid grid-cols-2 gap-2', anyYouTubeVotes ? 'sm:grid-cols-5' : 'sm:grid-cols-4')}>
        <StatTile label={anyYouTubeVotes ? 'Twitch votes' : 'Total votes'} value={totalVotes} />
        {anyYouTubeVotes && <StatTile label='YouTube votes' value={youtubeVotes} />}
        <StatTile label='Polls w/ votes' value={items.filter((i) => i.kind === 'poll').length} />
        <StatTile label='Tugs w/ votes' value={items.filter((i) => i.kind === 'tug').length} />
        <StatTile label='Duration' value={formatDuration(event.startedAt, event.endedAt)} />
      </div>

      {items.length === 0
        ? (
          <div className='rounded-lg border border-dashed border-white/10 px-4 py-8 text-center text-sm text-dm-text-secondary'>
            No votes were cast during this stream.
          </div>
          )
        : rightColHasContent
          ? (
            <div className='grid grid-cols-1 gap-5 md:grid-cols-2'>
              <div>
                <SubLabel>Results</SubLabel>
                {resultsCarousel}
              </div>
              <div className='space-y-5'>
                {superlatives !== null && (
                  <div>
                    <SubLabel>Superlatives</SubLabel>
                    <div className='grid grid-cols-2 gap-2'>
                      <SuperlativeTile
                        label='Most votes'
                        value={superlatives.mostVotes.name}
                        detail={`${superlatives.mostVotes.totalVotes} vote${superlatives.mostVotes.totalVotes === 1 ? '' : 's'}${youtubeTag(superlatives.mostVotes.platform)}`}
                      />
                      <SuperlativeTile
                        label='Closest call'
                        value={superlatives.closest.name}
                        detail={`${superlatives.closest.margin} pt margin${youtubeTag(superlatives.closest.platform)}`}
                      />
                      <SuperlativeTile
                        label='Landslide'
                        value={superlatives.landslide.name}
                        detail={`${superlatives.landslide.margin} pt margin${youtubeTag(superlatives.landslide.platform)}`}
                      />
                      <SuperlativeTile
                        label='Avg votes'
                        value={anyYouTubeVotes ? `Twitch ${avgOf('twitch')} · YouTube ${avgOf('youtube')}` : String(avgVotes)}
                        detail={anyYouTubeVotes ? 'per voted element, each platform' : 'per voted element'}
                      />
                    </div>
                  </div>
                )}
                {boardsWithScores.length > 0 && (
                  <div>
                    <SubLabel>Final standings</SubLabel>
                    <div className='space-y-2'>
                      {sortByName(boardsWithScores, (b) => b.name, (b) => b.boardId).map((b) => <ScoreboardStandings key={b.boardId} board={b} />)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            )
          : (
            <div className='mx-auto max-w-2xl'>
              <SubLabel>Results</SubLabel>
              {resultsCarousel}
            </div>
            )}
      {trivias.length > 0 && (
        <div className='mt-5'>
          <SubLabel>Trivia</SubLabel>
          <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
            {sortByName(trivias, (t) => elementDisplayName(t.name, t.question), (t) => t.triviaId).map((t) => <TriviaRecapCard key={t.triviaId} trivia={t} />)}
          </div>
        </div>
      )}
    </section>
  )
}

export const OverviewPage = (): JSX.Element => {
  const { event, setEvent } = useEventDetail()
  const { eventId = '' } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const { streamToolsUser } = useAuth()
  const isEnded = event.status === 'ENDED'
  const { canManage } = useEventAccess()

  return (
    <div className='mt-2 grid grid-cols-1 gap-1.5 px-4 pb-4 xl:grid-cols-2'>
      {isEnded
        ? (
          <EventRecap event={event} />
          )
        : (
          <LiveControlCard event={event} />
          )}
      {isEnded && event.trivias.length > 0 && (
        <TriviaLeaderboardPanel eventId={event.eventId} />
      )}
      {isEnded && canManage && (
        <SectionCard title='Security'>
          <p className='text-[11px] leading-relaxed text-dm-text-accent'>
            This stream has ended, but its stream key keeps working until
            it is reset. If the key leaked, reset it here.
          </p>
          <ResetStreamKeyControl eventId={event.eventId} />
        </SectionCard>
      )}
      <ConnectedSourcesCard
        event={event}
        onForeignSourcesChange={(sources) => setEvent((prev) => (prev === null ? prev : { ...prev, youtubeForeignSources: sources }))}
      />
      <ContentSummaryCard event={event} />
      <UserAccessCard
        event={event}
        currentUserId={streamToolsUser?.userId}
        currentDisplayName={streamToolsUser?.name}
        currentEmail={streamToolsUser?.email}
        onManage={() => { void navigate(eventSectionPath(eventId, 'access')) }}
      />
    </div>
  )
}
